import React, { useEffect, useState } from 'react'
import { InputComponent, UploadComponent, BtnComponent } from 'LibNeo'
import { Validators } from 'Modules'
import { FieldNames } from 'Constants/FieldNames'
import { WrappedFormUtils } from 'antd/lib/form/Form'
import { useAppDispatch } from 'Src/hooks'
import { useSelector } from 'react-redux'
import { Access } from 'Constants/FunctionAliases'
import { useHasAccess } from 'Src/hooks/useAccess'
import {
	BgDeleteDocument,
	bgDocumentSelector,
	bgDocumentSlice,
	BgSaveDocument,
	HiddenStyled,
	KindType,
	PROTECTED_DOCUMENTS,
	RenderUploadPreviewProps,
	UploadSetting,
} from 'Pages/ibro'
import { UploadPreviewComponent } from 'LibNeo/components/Basic/Upload/UploadPreview.component'
import {
	BgUploadDisabledWrapper,
	BgUploadWrapper,
} from 'Pages/ibro/common/components/BgUpload/styled'
import { Callback } from 'Src/types/utilTypes'
import { ReceivingFileModel } from 'Models'

interface BgUploadProps<T> {
	form: WrappedFormUtils
	item: T
	fieldName: FieldNames
	requiredFields?: FieldNames[]
	saveDocument: Callback<BgSaveDocument>
	deleteDocument: Callback<BgDeleteDocument>
	withoutFileCash?: boolean
	renderUploadPreview?: Callback<RenderUploadPreviewProps, JSX.Element & React.ReactNode>
	initialValue: ReceivingFileModel
	uploadSettings: UploadSetting
}

const actions = {
	addDocumentLoading: bgDocumentSlice.actions.addDocumentLoading,
	removeDocumentLoading: bgDocumentSlice.actions.removeDocumentLoading,
}

export const BgUpload = <T extends { id; required_fields; locked_fields; upload_files_messages? }>({
	fieldName,
	form,
	requiredFields,
	item,
	saveDocument,
	deleteDocument,
	withoutFileCash = true,
	initialValue,
	renderUploadPreview,
	uploadSettings,
}: BgUploadProps<T>) => {
	const documentsLoading = useSelector(bgDocumentSelector.selectDocumentsLoading)
	const { addDocumentLoading, removeDocumentLoading } = useAppDispatch(actions)
	const [serverError, setServerError] = useState('')
	const hasAccess = useHasAccess()

	const { locked_fields = [] } = item
	const required_fields = requiredFields || item.required_fields
	const errorsForm = form.getFieldsError()
	const hasDocAccess = !(
		PROTECTED_DOCUMENTS.includes(fieldName) && !hasAccess([Access.broker.document])
	)
	const isDisabled = !hasDocAccess || locked_fields.includes(fieldName)
	const isHideDropzone = Boolean(item[fieldName]) || documentsLoading.has(KindType[fieldName])

	const errorText =
		!isDisabled &&
		hasAccess([Access.broker.document]) &&
		(serverError ||
			errorsForm[fieldName]?.join() ||
			('upload_files_messages' in item && item[fieldName]?.upload_file_error))

	const showDisabledBtn = !hasDocAccess || (!item[fieldName] && locked_fields.includes(fieldName))

	useEffect(() => {
		if (!documentsLoading.has(KindType[fieldName])) {
			form.setFields({
				[fieldName]: {
					value: item[fieldName] || undefined,
					touched: true,
				},
			})
			form.validateFields([fieldName])
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentsLoading.has(KindType[fieldName])])

	const actionSave = (fileName, file) => {
		if (serverError) setServerError('')

		if (file) {
			saveDocument({
				data: {
					request_id: item.id,
					kind: KindType[fieldName],
					file,
					file_file_name: fileName,
				},
				id: item[fieldName]?.id,
				callback: () => addDocumentLoading(KindType[fieldName]),
				errorCallback: (err: string) => {
					setServerError(err)
				},
			})
		}
		if (!file) {
			deleteDocument({
				id: item[fieldName].id,
				request_id: item.id,
				fieldName,
			})
		}
	}

	const rules = item[fieldName]?.upload_file_error
		? [
				{
					validator: Validators.checkHasServerError({
						required: required_fields.includes(fieldName),
						payload: {
							text: item[fieldName]?.upload_file_error ?? undefined,
						},
					}),
				},
		  ]
		: [
				{
					validator: Validators.checkRequired({
						required: required_fields.includes(fieldName),
					}),
				},
		  ]

	if (showDisabledBtn)
		return initialValue ? (
			<BgUploadDisabledWrapper>
				<UploadPreviewComponent fileList={[initialValue]} deleteFile={null} fixWidth />
			</BgUploadDisabledWrapper>
		) : (
			<BtnComponent disabled icon="upload" title="Загрузить файл" outline />
		)

	return (
		<>
			<HiddenStyled>
				<InputComponent
					form={form}
					fieldName={fieldName}
					fieldOptions={{
						rules,
						initialValue: item[fieldName] || undefined,
					}}
				/>
			</HiddenStyled>
			<BgUploadWrapper>
				<UploadComponent
					isViewDeleteIcon
					widthValue={560}
					btnTitle="Загрузить файл"
					acceptedFiles={uploadSettings.accepted}
					maxFileSize={uploadSettings.maxSize}
					loading={documentsLoading.has(KindType[fieldName])}
					disabled={isDisabled}
					initialValue={initialValue}
					actionCreate={actionSave}
					actionDelete={actionSave}
					noAutoUpload={!hasDocAccess || withoutFileCash}
					onAutoUpload={() => removeDocumentLoading(KindType[fieldName])}
					errorText={errorText}
					isHideDropzone={isHideDropzone}
				>
					{withoutFileCash &&
						item[fieldName] &&
						renderUploadPreview?.({
							isError: Boolean(errorText),
							loading: documentsLoading.has(KindType[fieldName]),
							deleteFile: !isDisabled
								? () => {
										deleteDocument({
											id: item[fieldName].id,
											request_id: item.id,
											fieldName,
										})
								  }
								: undefined,
						})}
				</UploadComponent>
			</BgUploadWrapper>
		</>
	)
}
